import { useMemo } from "react";

interface HeroBackdropProps {
  /** Show the ASCII ticker rows + corner coordinate chips. Default true. */
  showChrome?: boolean;
  className?: string;
}

const CELLS = 18;
const ROWS = 12;

// Precomputed graticule coordinates + isoline radii — value-based map keys
// (these arrays are static and never reorder).
const V_LINES = Array.from({ length: CELLS + 1 }, (_, i) => (i / CELLS) * 1600);
const H_LINES = Array.from({ length: ROWS + 1 }, (_, i) => (i / ROWS) * 900);
const ISO_RADII = [80, 130, 180, 230, 280, 330, 380, 430, 480, 530];

// Chicago neighborhood labels — the home metro nod. Swap freely per campaign city.
const PLACE_LABELS: Array<{ x: number; y: number; text: string; anchor?: string }> = [
  { x: 1340, y: 280, text: "LAKE", anchor: "middle" },
  { x: 1340, y: 296, text: "MICHIGAN", anchor: "middle" },
  { x: 180, y: 180, text: "HUMBOLDT" },
  { x: 360, y: 240, text: "WICKER PARK" },
  { x: 560, y: 180, text: "LOGAN SQUARE" },
  { x: 780, y: 320, text: "WEST LOOP" },
  { x: 940, y: 420, text: "LOOP" },
  { x: 280, y: 600, text: "LITTLE VILLAGE" },
  { x: 560, y: 540, text: "PILSEN" },
  { x: 840, y: 660, text: "BRONZEVILLE" },
  { x: 120, y: 420, text: "GARFIELD" },
  { x: 560, y: 80, text: "LINCOLN PARK" },
  { x: 960, y: 180, text: "RIVER NORTH" },
];

const HWY_LABELS: Array<{ x: number; y: number; text: string }> = [
  { x: 800, y: 420, text: "I-90" },
  { x: 200, y: 780, text: "I-55" },
  { x: 1080, y: 820, text: "I-94" },
];

const TICKERS = [
  "FLOSCORE.AVG  74.8  ▲ 1.2   MULTIFAMILY  2,000 SCORED   CONFIDENCE.HIGH 38%",
  "CHI.METRO   ▲ 92    LOOP.WALKSCORE  ◆ 88    OHARE.ACCESS  ▼ 54   YP.FIT ◆ 71",
  "ZONING.RT4  142 UNITS   AADT 28,400   TAPESTRY · TRENDSETTERS · URBAN.CHIC",
  "ATLAS.RUN  /campaigns/new  ▸  geocoded 1942/2000  ▸  scoring 13 cat · 60 metrics",
];

export function HeroBackdrop({ showChrome = true, className }: HeroBackdropProps) {
  // Deterministic pulsing "data points" — skip the lake region on the right.
  const points = useMemo(() => {
    const arr: Array<{ x: number; y: number; kind: "high" | "med" | "low"; delay: number }> = [];
    for (let i = 0; i < 60; i++) {
      const x = ((Math.sin(i * 1.9) + 1) / 2) * 100;
      const y = ((Math.cos(i * 2.7) + 1) / 2) * 100;
      const lakeCutoff = 70 + (y / 100) * 15;
      if (x > lakeCutoff) continue;
      const lvl = (i * 7) % 11;
      arr.push({
        x,
        y,
        kind: lvl < 3 ? "high" : lvl < 7 ? "med" : "low",
        delay: (i * 0.27) % 4,
      });
    }
    return arr;
  }, []);

  return (
    <div className={`hb-backdrop ${className ?? ""}`} aria-hidden="true">
      <div className="hb-vignette" />

      <svg
        className="hb-svg hb-grid"
        viewBox="0 0 1600 900"
        preserveAspectRatio="xMidYMid slice"
        aria-hidden="true"
      >
        {V_LINES.map((x) => (
          <line key={`v${x}`} x1={x} y1="0" x2={x} y2="900" className="hb-line" />
        ))}
        {H_LINES.map((y) => (
          <line key={`h${y}`} x1="0" y1={y} x2="1600" y2={y} className="hb-line" />
        ))}

        {/* Topo isolines */}
        {ISO_RADII.map((r, i) => (
          <path
            key={`iso${r}`}
            d={`M -100,${450 + Math.sin(i * 0.9) * 60} q 320,${-r * 0.9} 720,0 t 720,0`}
            fill="none"
            className="hb-iso"
          />
        ))}

        {/* Coastline / lake region */}
        <path
          d="M 1100 60 C 1260 220, 1180 340, 1320 480 S 1240 720, 1380 880 L 1600 900 L 1600 0 L 1100 0 Z"
          className="hb-coast-fill"
        />
        <path
          d="M 0 720 C 120 690, 240 730, 360 700 S 600 680, 720 720 S 960 700, 1080 740"
          fill="none"
          className="hb-coast-dash"
          strokeDasharray="3 4"
        />

        <g className="hb-labels" fontFamily="var(--font-mono)" fontSize="12" letterSpacing="1.2">
          {PLACE_LABELS.map((l) => (
            <text key={l.text + l.y} x={l.x} y={l.y} textAnchor={l.anchor as "middle" | undefined}>
              {l.text}
            </text>
          ))}
        </g>
        <g
          className="hb-labels-hwy"
          fontFamily="var(--font-mono)"
          fontSize="11"
          letterSpacing="1.5"
        >
          {HWY_LABELS.map((l) => (
            <text key={l.text} x={l.x} y={l.y}>
              {l.text}
            </text>
          ))}
        </g>
      </svg>

      <div className="hb-fade" />

      <div className="hb-points">
        {points.map((p) => (
          <span
            key={`${p.x}-${p.y}`}
            className={`hb-pt hb-pt-${p.kind}`}
            style={{ left: `${p.x}%`, top: `${p.y}%`, animationDelay: `${p.delay}s` }}
          />
        ))}
      </div>

      {showChrome && (
        <>
          <div className="hb-coord hb-coord-tl">41.8781°N · 87.6298°W</div>
          <div className="hb-coord hb-coord-tr">LIVE</div>
          <div className="hb-coord hb-coord-bl">FLOSCORE.IDX</div>
          <div className="hb-coord hb-coord-br">/CAMPAIGN/NEW</div>
          <div className="hb-ticker-col">
            {TICKERS.map((row, i) => (
              <div key={row} className="hb-ticker-row" style={{ animationDelay: `${i * -7}s` }}>
                {`${row}    ${row}`}
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
